import { autorun, makeAutoObservable, runInAction } from 'mobx';
import {
  createProject,
  getAllProjects,
  updateProject,
  deleteProject,
  partialUpdateProject,
  getAllIterations,
  createProjectIteration,
  deleteProjectIteration,
  createProjectDocument,
  deleteProjectDocunent,
  addPerformerToProject,
  deletePerformerToProject,
} from '../../services/projects.services';
import { stringSorting } from '../../utils/transform';

const NAME = 'projects-store';
export class ProjectsStore {
  projects = [];
  iterations = [];
  selectedProject = null;
  selectedIteration = null;
  isLoading = false;

  constructor() {
    makeAutoObservable(this);

    const storeJSON = localStorage.getItem(NAME);

    if (storeJSON) Object.assign(this, JSON.parse(storeJSON));

    autorun(() => {
      const obj = JSON.parse(JSON.stringify(this));
      obj.isLoading = false;
      localStorage.setItem(NAME, JSON.stringify(obj));
    });

    window.addEventListener('storage', this._updateStore);
  }

  _updateStore = (e) => {
    if (e.key === NAME) {
      const storeJSON = localStorage.getItem(NAME);

      if (storeJSON) Object.assign(this, JSON.parse(storeJSON));
    }
  };

  _dispose = () => {
    window.removeEventListener('storage', this._updateStore);
  };

  _createProject = (object) => createProject(object);

  _updateProject = (object) => updateProject(object);

  _createProjectIteration = (data) => createProjectIteration(data);

  _createProjectDocument = (id, file) => createProjectDocument(id, file);

  _addPerformerToProject = (projectId, userId) => addPerformerToProject(projectId, userId);

  setProjects = async ({ search = undefined } = {}) => {
    const response = await getAllProjects({
      search,
      sorting: { field: 'name', type: 'asc' },
    });

    runInAction(() => {
      this.projects = response.data;
    });
  };

  upsertProject = async (data) => {
    let response;
    if (this.selectedProject?.id) response = await this._updateProject(data);
    else response = await this._createProject(data);

    if (response.success) {
      runInAction(() => {
        if (this.selectedProject?.id) {
          this.projects = stringSorting(
            this.projects.reduce((acc, current) => {
              if (current.id === response.result.data.id)
                current = {
                  ...response.result.data,
                };
              acc.push(current);
              return acc;
            }, []),
            'name',
          );
          this.selectedProject = { ...this.selectedProject, ...response.result.data };
        } else {
          this.projects = stringSorting([...this.projects, response.result.data], 'name');
        }
      });
    }

    return response;
  };

  removeProject = async () => {
    const response = await deleteProject(this.selectedProject.id);
    if (response.success) {
      runInAction(() => {
        this.projects = this.projects.filter((project) => project.id !== this.selectedProject.id);
      });
    }

    return response;
  };

  setSelectedProject = (obj) => {
    runInAction(() => (this.selectedProject = obj));
  };

  resetProject = () =>
    runInAction(() => {
      this.selectedProject = null;
    });

  upsertProjectScheme = async ({ id_scheme }) => {
    const response = await partialUpdateProject({
      id: this.selectedProject.id,
      id_scheme: id_scheme,
    });
    if (response.success) {
      this.setSelectedProject(response.result.data);
    }
    return response;
  };

  setSelectedIteration = (obj) => {
    runInAction(() => (this.selectedIteration = obj));
  };

  upsertProjectDevices = async (devices) => {
    const transformData = Object.values(devices)
      .filter((deviceId) => deviceId)
      .map((deviceId) => ({
        id_device: deviceId,
      }));
    const response = await partialUpdateProject({
      id: this.selectedProject.id,
      devices: transformData,
    });
    if (response.success) {
      this.setSelectedProject(response.result.data);
    }
    return response;
  };

  setIteration = async ({ search = undefined } = {}) => {
    const response = await getAllIterations({
      search,
      sorting: { field: 'created_at', type: 'desc' },
    });
    runInAction(() => {
      this.iterations = response.result.data;
    });
  };

  upsertProjectIteration = async (data) => {
    const response = await this._createProjectIteration(data);
    if (response.success) {
      runInAction(() => {
        const sortedIterations = [...(this.selectedProject.iterations || []), response.result.data];
        this.selectedProject.iterations = stringSorting(sortedIterations, 'id');
      });
    }
    return response;
  };

  removeProjectIteration = async (id) => {
    const response = await deleteProjectIteration(id);
    if (response.success) {
      runInAction(() => {
        this.selectedProject.iterations = this.selectedProject.iterations.filter(
          (iteration) => iteration.id !== id,
        );
      });
    }
    return response;
  };

  upsertProjectDocument = async (id, file) => {
    const response = await this._createProjectDocument(id, file);
    if (response.success) {
      runInAction(() => {
        this.selectedProject.files = [
          ...(this.selectedProject.files || []),
          ...response.result.data,
        ];
      });
    }
    return response;
  };

  removeProjectDocument = async (id) => {
    const response = await deleteProjectDocunent(id);
    if (response.success) {
      runInAction(() => {
        this.selectedProject.files = this.selectedProject.files.filter((file) => file.id !== id);
      });
    }
    return response;
  };

  setIsLoading = (value) => {
    this.isLoading = value;
  };

  upsertProjectPerformer = async (projectId, userId) => {
    const response = await this._addPerformerToProject(projectId, userId);
    if (response.success) {
      runInAction(() => {
        this.selectedProject.users = [...(this.selectedProject.users || []), response.result.data];
      });
    }
    return response;
  };

  removeProjectPerformer = async (projectId, userId) => {
    const response = await deletePerformerToProject(projectId, userId);
    if (response.success) {
      runInAction(() => {
        this.selectedProject.users = this.selectedProject.users.filter(
          (user) => user.id !== userId,
        );
      });
    }
    return response;
  };
}

export const projectsStore = new ProjectsStore();
